import React, { useState, useRef, useEffect, useCallback } from 'react';
import { samTTS } from './utils/samTTS';
import { EchoFilter } from './utils/EchoFilter';
import { AUTOPLAY_FALLBACK, buildGreeting, firstName, microphoneControlLabel, microphoneErrorText, microphoneStatusText, shouldRequestMicResume, speechRecognitionSupported, voiceControlLabel } from './utils/helpAgentSession';
import { AssistantMessage, UserMessage } from './components/ChatMessage';
import { resolveMessageText } from './utils/sendMessage';
import { resolveHelpApiBase } from './utils/helpApiBase';
import { createSessionVoiceResolver } from './utils/helpVoiceSession';
import { createNarrationQueue, narrationCompletionOwnsUi, shouldShowAutoplayFallback } from './utils/narrationQueue';
import { HELP_APP_RELEASE } from './helpRelease';

/**
 * Sam Help Agent — Popup Chat App
 *
 * Standalone micro-app in a named browser popup window.
 * Uses samTTS (shared kernel TTS interface) for voice output.
 */

// ─── Config ────────────────────────────────────────────────────────────────

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const HELP_API = resolveHelpApiBase(window.location.hostname, process.env.REACT_APP_HELP_API_BASE);
const DBAM_API = isLocal ? 'http://localhost:8080' : 'https://api.samsurveyor.com';

// ─── Initial params ──────────────────────────────────────────────────────

function getInitialParams() {
    const qp = new URLSearchParams(window.location.search);
    return {
        userId: qp.get('userId') || null,
        userName: qp.get('userName') || null,
        context: qp.get('context') || null,
    };
}

// ─── App ───────────────────────────────────────────────────────────────────

export default function App() {
    const initialParamsRef = useRef(getInitialParams());
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [context, setContext] = useState(initialParamsRef.current.context || 'console');
    const [userId, setUserId] = useState(initialParamsRef.current.userId);
    const [userName, setUserName] = useState(firstName(initialParamsRef.current.userName));
    const [identityResolved, setIdentityResolved] = useState(!!initialParamsRef.current.userName);
    const [historySummary, setHistorySummary] = useState(null);
    const [sessionMessages, setSessionMessages] = useState([]);
    const [isVoiceMuted, setIsVoiceMuted] = useState(false);
    const [isMicMuted, setIsMicMuted] = useState(false);
    const [micResumeNonce, setMicResumeNonce] = useState(0);
    const [voiceStartBlocked, setVoiceStartBlocked] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [helpVoice, setHelpVoice] = useState(null);
    const [isListening, setIsListening] = useState(false);
    const [interimTranscript, setInterimTranscript] = useState('');
    // This must be synchronous: known-name launchers can resolve identity before
    // an effect runs, and the greeting must not consume auto-listening early.
    const [voiceSupported] = useState(() => speechRecognitionSupported(window));
    const [microphoneError, setMicrophoneError] = useState('');
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const recognitionRef = useRef(null);
    const silenceTimerRef = useRef(null);
    const finalBufferRef = useRef('');
    const sendMessageRef = useRef(null);
    const startListeningRef = useRef(null);
    const greetingStartedRef = useRef(false);
    const greetingRenderedAtRef = useRef(null);
    const stoppingRecognitionRef = useRef(false);
    const pendingMicResumeRef = useRef(false);
    const voiceMutedRef = useRef(false);
    const echoFilterRef = useRef(new EchoFilter({ threshold: 0.325, debug: true }));
    const voiceResolverRef = useRef(null);
    const narrationQueueRef = useRef(null);
    if (!voiceResolverRef.current) {
        voiceResolverRef.current = createSessionVoiceResolver(
            () => fetch(`${DBAM_API}/dbam/v1/config`),
            samTTS.DEFAULT_VOICE,
            1500,
            error => console.warn('Failed to load help voice preference:', error),
        );
    }
    if (!narrationQueueRef.current) {
        narrationQueueRef.current = createNarrationQueue({ tts: samTTS });
    }

    const requestMicResume = useCallback(() => {
        if (pendingMicResumeRef.current) return;
        pendingMicResumeRef.current = true;
        setMicResumeNonce(nonce => nonce + 1);
    }, []);

    // Use launcher-provided identity/context immediately, then resolve /me as
    // the credentialed fallback for Explorer and direct Help entry.
    useEffect(() => {
        fetch(`${DBAM_API}/dbam/v1/me`, { credentials: 'include' })
            .then(async res => res.ok ? res.json() : null)
            .then(payload => {
                const profile = payload?.data || payload;
                if (!profile) return;
                if (profile.user_id) setUserId(current => current || profile.user_id);
                const displayName = profile.name || profile.display_name || '';
                if (displayName) setUserName(current => current || firstName(displayName));
            })
            .catch(() => {
                // A direct Help popup can still be useful while auth/profile is unavailable.
            })
            .finally(() => setIdentityResolved(true));
    }, []);

    // Warm the kernel's lazy TTS client while identity/history load. This keeps
    // the first spoken answer from paying the connection-initialisation cost.
    useEffect(() => { samTTS.warm().catch(() => {}); }, []);

    // State changes from either mute button happen asynchronously. Resume from
    // this post-state effect rather than from a click handler's stale closure.
    useEffect(() => {
        if (!pendingMicResumeRef.current || isMicMuted || isSpeaking || loading || !voiceSupported) return;
        pendingMicResumeRef.current = false;
        startListeningRef.current?.();
    }, [isMicMuted, isSpeaking, loading, voiceSupported, micResumeNonce]);

    // SSE: subscribe to context changes from sam-dbam.
    // Each app POSTs its context on tab focus; the server pushes it here instantly.
    useEffect(() => {
        const uid = userId || 'default';
        const evtUrl = `${DBAM_API}/dbam/v1/help/context/${encodeURIComponent(uid)}/events`;
        const source = new EventSource(evtUrl);

        source.onmessage = (event) => {
            const ctx = event.data?.trim();
            if (ctx) setContext(ctx);
        };

        source.onerror = () => {
            // EventSource auto-reconnects — nothing to do
        };

        return () => source.close();
    }, [userId]);

    // Resolve the session voice exactly once. Visual Help remains immediate;
    // every spoken utterance waits for this preference or the safe fallback.
    useEffect(() => {
        let current = true;
        voiceResolverRef.current.resolve().then(voice => {
            if (current) setHelpVoice(voice);
        });
        return () => { current = false; };
    }, []);

    // Load conversation history on mount
    useEffect(() => {
        if (!userId) return;
        (async () => {
            try {
                const res = await fetch(`${DBAM_API}/dbam/v1/help/history/${userId}`, {
                    credentials: 'include',
                });
                if (!res.ok) return;
                const data = await res.json();
                const hist = data?.messages || data?.data?.messages || [];
                if (hist.length > 0) {
                    const recent = hist.slice(-20);
                    const summary = recent
                        .filter(m => m.role === 'user')
                        .map(m => {
                            const date = m.ts ? new Date(m.ts).toLocaleDateString() : 'unknown date';
                            const ctx = m.context ? ` in ${m.context}` : '';
                            return `- ${date}${ctx}: "${m.text}"`;
                        })
                        .join('\n');
                    setHistorySummary(summary);
                }
            } catch (e) {
                console.warn('Failed to load help history:', e);
            }
        })();
    }, [userId]);

    // Flush new messages to dbam on close + stop queued TTS requests/audio.
    useEffect(() => {
        const flush = () => {
            narrationQueueRef.current.cancel('window-close');
            if (!userId || sessionMessages.length === 0) return;
            const body = JSON.stringify({ messages: sessionMessages });
            navigator.sendBeacon(
                `${DBAM_API}/dbam/v1/help/history/${userId}`,
                new Blob([body], { type: 'application/json' }),
            );
        };
        window.addEventListener('beforeunload', flush);
        return () => window.removeEventListener('beforeunload', flush);
    }, [userId, sessionMessages]);

    useEffect(() => () => narrationQueueRef.current.cancel('unmount'), []);

    // Auto-scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    // Typed input remains available whether or not voice permission is granted.
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const sendMessage = async (spokenText) => {
        const text = resolveMessageText(spokenText, input);
        if (!text || loading) return;

        const userMsg = { role: 'user', text, context, ts: new Date().toISOString() };
        setMessages(prev => [...prev, userMsg]);
        setSessionMessages(prev => [...prev, userMsg]);
        setInput('');
        setLoading(true);

        // A new turn owns narration immediately; stale fetches/audio cannot continue.
        narrationQueueRef.current.cancel('new-answer');
        setIsSpeaking(false);

        try {
            const res = await fetch(`${HELP_API}/help/message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text,
                    context,
                    user_name: userName,
                    user_id: userId,
                    history_summary: historySummary,
                    conversation_history: messages.map(m => ({
                        role: m.role === 'sam' ? 'model' : 'user',
                        text: m.text,
                    })),
                }),
            });

            const data = await res.json();
            const responseText = data.response || 'Sorry, I had trouble processing that.';
            const samMsg = { role: 'sam', text: responseText, ts: new Date().toISOString() };
            const responseRenderedAt = performance.now();

            setMessages(prev => [...prev, samMsg]);
            setSessionMessages(prev => [...prev, samMsg]);

            // Speak the response (unless muted) with the same resolved voice as
            // the opening greeting and every other utterance in this session.
            if (!isVoiceMuted) {
                const resolvedVoice = await voiceResolverRef.current.resolve();
                if (voiceMutedRef.current) {
                    if (voiceSupported && !isMicMuted) requestMicResume();
                    return;
                }
                // Stop mic before TTS to prevent echo
                stopListening();
                setIsSpeaking(true);
                narrationQueueRef.current.start(responseText, {
                    voice: resolvedVoice,
                    context: 'help_agent',
                    renderedAt: responseRenderedAt,
                    onSanitized: responseNarration => echoFilterRef.current.trackSamUtterance(responseNarration),
                }).then(result => {
                    if (!narrationCompletionOwnsUi(result, narrationQueueRef.current.isActive())) return;
                    setIsSpeaking(false);
                    // Auto-listen after TTS — echo filter protects against pickup
                    if (voiceSupported && !isMicMuted) requestMicResume();
                });
            } else if (voiceSupported && !isMicMuted) {
                // Text-only Sam replies should return the conversation to the mic immediately.
                requestMicResume();
            }
        } catch (e) {
            setMessages(prev => [...prev, {
                role: 'sam',
                text: "I'm having trouble connecting right now. Please try again in a moment.",
                ts: new Date().toISOString(),
            }]);
            if (voiceSupported && !isMicMuted) requestMicResume();
        } finally {
            setLoading(false);
            inputRef.current?.focus();
        }
    };

    const greeting = buildGreeting(userName, context);

    // The greeting is always visible on first paint. Speech is opportunistic:
    // browser autoplay policies may reject it, but that never blocks typing or
    // automatic listening once the browser has microphone permission.
    useEffect(() => {
        if (!identityResolved || greetingStartedRef.current) return;
        if (greetingRenderedAtRef.current === null) greetingRenderedAtRef.current = performance.now();
        const beginListening = () => {
            if (voiceSupported && !isMicMuted) requestMicResume();
        };
        if (isVoiceMuted) {
            greetingStartedRef.current = true;
            beginListening();
            return;
        }
        if (!helpVoice) return;
        greetingStartedRef.current = true;
        setIsSpeaking(true);
        narrationQueueRef.current.start(greeting, {
            voice: helpVoice,
            context: 'help_agent',
            renderedAt: greetingRenderedAtRef.current,
            onSanitized: greetingNarration => echoFilterRef.current.trackSamUtterance(greetingNarration),
        })
            .then(result => {
                if (!narrationCompletionOwnsUi(result, narrationQueueRef.current.isActive())) return;
                if (shouldShowAutoplayFallback(result) && !voiceMutedRef.current) setVoiceStartBlocked(true);
                setIsSpeaking(false);
                beginListening();
            });
    // greetingStartedRef preserves one greeting while complete dependencies keep
    // the effect valid under the production CRA hooks lint rule.
    }, [greeting, helpVoice, identityResolved, isMicMuted, isVoiceMuted, requestMicResume, voiceSupported]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
    };

    const clearChat = () => {
        const shouldResume = shouldRequestMicResume({
            supported: voiceSupported,
            muted: isMicMuted,
            speaking: false,
            loading,
        });
        narrationQueueRef.current.cancel('clear');
        stopListening();
        setIsSpeaking(false);
        setMessages([]);
        if (shouldResume) requestMicResume();
    };

    // ─── Voice Input (STT) ──────────────────────────────────────────────────

    const startListening = () => {
        if (isMicMuted || loading || isSpeaking) return;
        if (recognitionRef.current) {
            stoppingRecognitionRef.current = true;
            recognitionRef.current.abort();
        }

        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            setMicrophoneError('Voice input is not available in this browser.');
            return;
        }

        // Stop TTS if speaking so the mic doesn't pick it up
        narrationQueueRef.current.cancel('microphone-start');
        setIsSpeaking(false);
        finalBufferRef.current = '';

        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        recognition.onstart = () => {
            stoppingRecognitionRef.current = false;
            setMicrophoneError('');
            setIsListening(true);
        };

        recognition.onresult = (event) => {
            let interim = '';
            let allFinal = '';
            for (let i = 0; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    allFinal += transcript;
                } else {
                    interim += transcript;
                }
            }

            finalBufferRef.current = allFinal;
            setInterimTranscript(interim);

            // Reset silence timer on every result
            if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);

            // After 1.5s of silence, send the accumulated final text
            if (allFinal.trim()) {
                silenceTimerRef.current = setTimeout(() => {
                    const text = finalBufferRef.current.trim();
                    if (text) {
                        finalBufferRef.current = '';
                        setInterimTranscript('');
                        recognition.abort();
                        // Echo filter: skip if this is Sam's own voice
                        if (echoFilterRef.current.isSamEcho(text)) {
                            console.log('[HelpAgent] Echo filtered:', text);
                            return;
                        }
                        setInput(text);
                        setTimeout(() => sendMessageRef.current?.(text), 50);
                    }
                }, 1500);
            }
        };

        recognition.onerror = (event) => {
            if (event.error !== 'no-speech' && event.error !== 'aborted') {
                console.error('[HelpAgent] Speech recognition error:', event.error);
                setMicrophoneError(microphoneErrorText(event.error));
            }
            stoppingRecognitionRef.current = true;
            recognitionRef.current = null;
            setIsListening(false);
            setInterimTranscript('');
        };

        recognition.onend = () => {
            setIsListening(false);
            setInterimTranscript('');
            if (stoppingRecognitionRef.current) {
                stoppingRecognitionRef.current = false;
                return;
            }
            // Send any remaining buffered text
            if (finalBufferRef.current.trim()) {
                const text = finalBufferRef.current.trim();
                finalBufferRef.current = '';
                if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
                // Echo filter check
                if (echoFilterRef.current.isSamEcho(text)) {
                    console.log('[HelpAgent] Echo filtered (onend):', text);
                    return;
                }
                setInput(text);
                setTimeout(() => sendMessageRef.current?.(text), 50);
            }
        };

        recognitionRef.current = recognition;
        try {
            recognition.start();
        } catch (error) {
            recognitionRef.current = null;
            stoppingRecognitionRef.current = false;
            setIsListening(false);
            setMicrophoneError(microphoneErrorText(error?.name));
        }
    };

    const stopListening = () => {
        if (recognitionRef.current) {
            stoppingRecognitionRef.current = true;
            recognitionRef.current.abort();
            recognitionRef.current = null;
        }
        setIsListening(false);
        setInterimTranscript('');
        if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    };

    // Keep refs current
    sendMessageRef.current = sendMessage;
    startListeningRef.current = startListening;

    const toggleVoiceMute = () => {
        if (!isVoiceMuted) {
            voiceMutedRef.current = true;
            narrationQueueRef.current.cancel('voice-muted');
            setIsSpeaking(false);
            if (voiceSupported && !isMicMuted) requestMicResume();
        } else {
            voiceMutedRef.current = false;
        }
        setIsVoiceMuted(m => !m);
    };

    const toggleMicMute = () => {
        if (!isMicMuted) {
            stopListening();
        } else if (!isSpeaking && !loading && voiceSupported) {
            requestMicResume();
        }
        setIsMicMuted(m => !m);
    };

    const hasMessages = messages.length > 0;
    const microphoneStatus = microphoneStatusText({
        supported: voiceSupported,
        listening: isListening,
        muted: isMicMuted,
        error: microphoneError,
    });

    return (
        <>
            <div className="help-header">
                <div className="help-header-title">
                    <span className="help-header-dot" />
                    Sam Help Agent
                    <span className="help-context-badge">{context}</span>
                    <span
                        className="help-build-marker"
                        data-help-build={HELP_APP_RELEASE}
                        aria-label={`Help build ${HELP_APP_RELEASE}`}
                        title={`Help build ${HELP_APP_RELEASE}`}
                    >
                        {HELP_APP_RELEASE}
                    </span>
                </div>
                <div className="help-header-actions">
                    <button
                        className={`help-header-btn ${isVoiceMuted ? 'help-muted' : ''}`}
                        onClick={toggleVoiceMute}
                        title={voiceControlLabel(isVoiceMuted)}
                        aria-label={voiceControlLabel(isVoiceMuted)}
                    >
                        {voiceControlLabel(isVoiceMuted)}
                    </button>
                    <button
                        className={`help-header-btn ${isMicMuted ? 'help-muted' : ''}`}
                        onClick={toggleMicMute}
                        title={microphoneControlLabel(isMicMuted)}
                        aria-label={microphoneControlLabel(isMicMuted)}
                    >
                        {microphoneControlLabel(isMicMuted)}
                    </button>
                    {hasMessages && (
                        <button className="help-header-btn" onClick={clearChat}>Clear</button>
                    )}
                    <button className="help-header-btn" onClick={() => { narrationQueueRef.current.cancel('close'); window.close(); }}>
                        Close
                    </button>
                </div>
            </div>

            {hasMessages ? (
                <div className="help-messages">
                    {messages.map((msg, i) => (
                        <div key={i} className={`help-msg ${msg.role === 'sam' ? 'help-msg-sam' : 'help-msg-user'}`}>
                            {msg.role === 'sam'
                                ? <AssistantMessage>{msg.text}</AssistantMessage>
                                : <UserMessage>{msg.text}</UserMessage>}
                        </div>
                    ))}
                    {loading && (
                        <div className="help-typing">
                            <div className="help-typing-dot" />
                            <div className="help-typing-dot" />
                            <div className="help-typing-dot" />
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>
            ) : (
                <div className="help-welcome">
                    <h2>{greeting}</h2>
                    <p>
                        {microphoneStatus}
                    </p>
                    {voiceStartBlocked && <p className="help-autoplay-notice" role="status">{AUTOPLAY_FALLBACK}</p>}
                </div>
            )}

            <div className="help-input-area">
                {hasMessages && (
                    <div className={`help-microphone-status ${microphoneError ? 'help-microphone-error' : ''}`} role="status">
                        {microphoneStatus}
                    </div>
                )}
                {isListening && interimTranscript && (
                    <div className="help-interim">{interimTranscript}</div>
                )}
                <div className="help-input-row">
                    <input
                        ref={inputRef}
                        className="help-input"
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={isListening ? 'Listening…' : 'Ask Sam a question…'}
                        disabled={loading}
                    />
                    <button
                        className="help-send-btn"
                        onClick={() => sendMessage()}
                        disabled={!input.trim() || loading}
                        title="Send"
                    >↑</button>
                </div>
            </div>
        </>
    );
}
